import {
  useGetEvent,
  useGetEventLocation,
  useGetSessionLocation,
} from '@sunrise-ui/api-client';
import {
  Colors,
  TextReg,
  useDynamicStyles,
  useMediaQueries,
  type WithResponsive,
} from '@sunrise-ui/primitives';
import React from 'react';
import { StyleSheet, View } from 'react-native';

type Props = {
  title?: string;
  eventId?: string;
  // If provided, show the SessionLocation name for this session (scoped by event)
  sessionLocationId?: string;
};

export function TimelineItem({ title, eventId, sessionLocationId }: Props) {
  const { isMobile } = useMediaQueries();
  const { data: event } = useGetEvent(eventId);
  // Session-level location support
  const { data: sessionLocation, isLoading: isLoadingSessionLocs } =
    useGetSessionLocation(sessionLocationId);
  // Fallback to event location only when not preferring session-only display
  const { data: eventLocation, isLoading: isLoadingEventLoc } =
    useGetEventLocation(
      !sessionLocationId ? event?.event_location_id : undefined,
    );

  const name =
    sessionLocation?.name ||
    (!sessionLocationId ? eventLocation?.name : undefined);
  const isLoading =
    isLoadingSessionLocs || (!sessionLocationId ? isLoadingEventLoc : false);

  const styles = useDynamicStyles(createStyles);

  return (
    <View style={styles.eventItem}>
      <View style={styles.titleRow}>
        <TextReg style={styles.eventTitle} numberOfLines={isMobile ? 3 : 2}>
          {title}
        </TextReg>
      </View>
      {name ? (
        <TextReg style={styles.eventLocation} numberOfLines={1}>
          {name}
        </TextReg>
      ) : isLoading ? (
        <View style={styles.locationSkeleton} />
      ) : null}
    </View>
  );
}

const createStyles = ({ isMobile }: WithResponsive) =>
  StyleSheet.create({
    eventItem: {
      backgroundColor: Colors.purple0,
      flex: 1,
      borderRadius: 8,
      padding: 8,
      borderLeftWidth: 4,
      borderLeftColor: Colors.yellow0,
      borderColor: Colors.yellow0,
      borderWidth: 1,
      width: '100%',
      overflow: 'hidden',
    },
    eventTitle: {
      fontSize: isMobile ? 10 : 12,
    },
    titleRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
    },
    eventLocation: {
      fontSize: 12,
      color: Colors.yellow0,
    },
    locationSkeleton: {
      maxWidth: 300,
      height: 11.5,
      backgroundColor: Colors.opacity20,
      borderRadius: 8,
      marginBottom: 4,
      marginTop: 2,
      marginRight: 5,
    },
    eventSummary: {
      fontSize: 12,
    },
  });
